<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="chain-node"
    :class="nodeClass"
    v-bind="to ? { to } : {}"
  >
    <span class="chain-node__icon" :class="iconClass">
      <v-icon small>{{ icon }}</v-icon>
    </span>
    <span class="chain-node__body">
      <span class="chain-node__kind">
        {{ kindLabel }}
        <template v-if="directionLabel"> · {{ directionLabel }}</template>
      </span>
      <span class="chain-node__code">{{ chip.code }}</span>
      <span v-if="meta" class="chain-node__meta">{{ meta }}</span>
    </span>
  </component>
</template>

<script>
import { ROUTE_BY_KIND, kindIcon, kindVariant } from "@/views/pages/deals/dealWorkflowMeta";

const KIND_KEYS = {
  purchase_order: "DEALS.VIEW.CHAIN_NODE_PO",
  sales_order: "DEALS.VIEW.CHAIN_NODE_SO",
  delivery_note: "DEALS.VIEW.CHAIN_NODE_DN",
  transport_order: "DEALS.VIEW.CHAIN_NODE_TO",
};

export default {
  name: "DealTradeChainNode",
  props: {
    chip: { type: Object, required: true },
  },
  computed: {
    to() {
      if (!this.chip.can_open || !ROUTE_BY_KIND[this.chip.kind]) {
        return null;
      }
      return { name: ROUTE_BY_KIND[this.chip.kind], params: { id: this.chip.id } };
    },
    variant() {
      return kindVariant(this.chip.kind);
    },
    direction() {
      if (this.chip.kind !== "delivery_note") {
        return "";
      }
      return this.chip.direction === "inbound" || this.chip.direction === "outbound"
        ? this.chip.direction
        : "";
    },
    nodeClass() {
      return [
        `chain-node--${this.variant}`,
        this.direction && `chain-node--${this.direction}`,
        !this.to && "chain-node--locked",
      ];
    },
    iconClass() {
      return [
        `chain-node__icon--${this.variant}`,
        this.direction && `chain-node__icon--${this.direction}`,
      ];
    },
    icon() {
      return kindIcon(this.chip.kind);
    },
    kindLabel() {
      const key = KIND_KEYS[this.chip.kind];
      return key ? this.$t(key) : this.chip.kind;
    },
    directionLabel() {
      if (!this.direction) {
        return "";
      }
      return this.$t(this.direction === "inbound" ? "DEALS.VIEW.INBOUND" : "DEALS.VIEW.OUTBOUND");
    },
    meta() {
      const quantity = this.chip.quantity == null ? "" : String(Number(this.chip.quantity));
      return [this.chip.status, quantity].filter(Boolean).join(" · ");
    },
  },
};
</script>

<style lang="scss" scoped>
.chain-node {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  width: 100%;
  max-width: 188px;
  min-width: 148px;
  padding: 7px 8px 7px 7px;
  background: #fff;
  border: 1px solid #b7c0cb;
  border-left-width: 3px;
  color: inherit;
  text-align: left;
  text-decoration: none;
  box-shadow: 0 1px 0 rgba(28, 36, 46, 0.05);
  position: relative;
  z-index: 2;
}

.chain-node--locked {
  cursor: default;
}

.chain-node--purchase-order {
  border-left-color: #3949ab;
}

.chain-node--sales-order {
  border-left-color: #00897b;
}

.chain-node--transport {
  border-left-color: #0288d1;
}

.chain-node--delivery-note {
  border-left-color: #00695c;
}

.chain-node--inbound {
  border-left-color: #3949ab;
  background: #f6f7fc;
}

.chain-node--outbound {
  border-left-color: #00897b;
  background: #f3faf8;
}

.chain-node__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 8px;
}

.chain-node__icon--purchase-order {
  background: rgba(57, 73, 171, 0.1);
  color: #3949ab;
}

.chain-node__icon--sales-order {
  background: rgba(0, 137, 123, 0.1);
  color: #00897b;
}

.chain-node__icon--transport {
  background: rgba(2, 136, 209, 0.1);
  color: #0288d1;
}

.chain-node__icon--delivery-note {
  background: rgba(0, 105, 92, 0.1);
  color: #00695c;
}

.chain-node__icon--inbound {
  background: rgba(57, 73, 171, 0.12);
  color: #3949ab;
}

.chain-node__icon--outbound {
  background: rgba(0, 137, 123, 0.12);
  color: #00897b;
}

.chain-node__icon .v-icon {
  color: inherit !important;
}

.chain-node__body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.chain-node__kind {
  font-size: 11px;
  line-height: 1.25;
  color: #5d6b7a;
}

.chain-node--inbound .chain-node__kind {
  color: #3949ab;
}

.chain-node--outbound .chain-node__kind {
  color: #00897b;
}

.chain-node__code {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  color: #1c2430;
  overflow-wrap: anywhere;
}

.chain-node__meta {
  margin-top: 1px;
  font-size: 11px;
  line-height: 1.2;
  color: #7b8794;
}
</style>
